import type { ComponentConfig } from '@measured/puck';
import { ArrowRight } from 'lucide-react';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { ROUTES } from '@shared/constants/routes';

export interface HeroCalculatorProps {
  title: string;
  subtitle?: string;
  calculator: 'cee' | 'comparateur' | 'aides' | 'dt';
}

const CALCULATOR_META: Record<
  HeroCalculatorProps['calculator'],
  { label: string; href: string; cta: string; blurb: string }
> = {
  cee: {
    label: 'Estimateur CEE',
    href: ROUTES.diagnostic,
    cta: 'Lancer le diagnostic',
    blurb: 'SIRET → estimation chiffrée des primes en 60 secondes.',
  },
  comparateur: {
    label: 'Comparateur d’énergie',
    href: ROUTES.comparateurDemarrer,
    cta: 'Comparer mes offres',
    blurb: "Courtier indépendant des fournisseurs d'énergie — comparatif sous 24 h.",
  },
  aides: {
    label: 'Calculateur d’aides',
    href: ROUTES.calculerMesAides,
    cta: 'Simuler mes aides',
    blurb: "MaPrimeRénov', primes CEE, éco-PTZ et TVA 5,5 % cumulés.",
  },
  dt: {
    label: 'Diagnostic Décret Tertiaire',
    href: ROUTES.decretTertiaire,
    cta: 'Lancer le diagnostic',
    blurb: 'Trajectoire −40 % 2030 et plan d’actions financé par les primes.',
  },
};

export function HeroCalculator({ title, subtitle, calculator }: HeroCalculatorProps) {
  const meta = CALCULATOR_META[calculator];
  return (
    <section className="bg-gradient-to-br from-brand-50 to-stone-50 py-14">
      <Container>
        <div className="grid items-center gap-10 lg:grid-cols-[1.1fr,1fr]">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tightish md:text-5xl">{title}</h1>
            {subtitle ? (
              <p className="mt-4 max-w-prose text-lg text-stone-700">{subtitle}</p>
            ) : null}
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">
              {meta.label}
            </p>
            <p className="mt-2 text-sm text-stone-600">{meta.blurb}</p>
            <Button asChild variant="brand" size="lg" className="mt-5 w-full sm:w-auto">
              <a href={meta.href}>
                {meta.cta}
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </Container>
    </section>
  );
}

export const HeroCalculatorConfig: ComponentConfig<HeroCalculatorProps> = {
  fields: {
    title: { type: 'textarea' },
    subtitle: { type: 'textarea' },
    calculator: {
      type: 'select',
      options: [
        { label: 'CEE', value: 'cee' },
        { label: 'Comparateur', value: 'comparateur' },
        { label: 'Aides rénovation', value: 'aides' },
        { label: 'Décret Tertiaire', value: 'dt' },
      ],
    },
  },
  defaultProps: {
    title: 'Calculez vos primes en 60 secondes',
    subtitle: 'Outils signature, chiffres documentés, sources officielles et fiches CEE vérifiées.',
    calculator: 'cee',
  },
  render: HeroCalculator,
};
